import { __awaiter, __generator } from "tslib";
import { Color4 } from "@babylonjs/core/Maths/math.color";
import { Vector2 } from "@babylonjs/core/Maths/math.vector";
import { ListenableEvent } from "../../utils";
import { Lock } from "../../utils/async";
import { ERR_INVALID_ARGUMENT, newError } from "../../utils/error";
import { canvasToBlob } from "../../utils/image";
import { clamp } from "../../utils/math";
import { InputState, PointerInfo } from "./InputState";
import { View, ViewLayerMaskFlags } from "./View";
var Viewer = /** @class */ (function () {
    function Viewer(pApp, pContainer) {
        var _this = this;
        this.eventResize = new ListenableEvent(this);
        this.eventBeforeRender = new ListenableEvent(this);
        this.eventAfterRender = new ListenableEvent(this);
        //// internal //////////////////////////////////////////////////////////////////////////////
        /** @internal */
        this._renderOnlyScreenshotMeshes = false;
        this.resetDoubleClickState = function () {
            _this.mDoubleClickCounter = 0;
            if (_this.mDoubleClickTimer != null) {
                clearTimeout(_this.mDoubleClickTimer);
                _this.mDoubleClickTimer = null;
            }
        };
        this.onResize = function () {
            _this.resize();
        };
        this.onPointerDown = function (pEvent) {
            if (_this.mInputState.registerPointer(pEvent)) {
                _this.mTools._handlePointerDown(pEvent, _this.mInputState);
                _this.mView.cameraControl._handlePointerDown(pEvent, _this.mInputState);
                _this.mCanvas.setPointerCapture(pEvent.pointerId);
                if (document.activeElement != _this.mCanvas)
                    _this.mCanvas.focus({ preventScroll: true }); //set focus on canvas
                //double click handling
                if (_this.mDoubleClickCounter === 0) {
                    _this.mDoubleClickTimer = window.setTimeout(_this.resetDoubleClickState, Viewer.MAX_DOUBLE_CLICK_DELAY);
                }
                _this.mDoubleClickCounter++;
                _this.createPointerFeedback(pEvent);
                pEvent.preventDefault();
            }
        };
        this.onPointerUp = function (pEvent) {
            if (_this.mInputState.updatePointer(pEvent)) {
                _this.mTools._handlePointerUp(pEvent, _this.mInputState);
                _this.mView.cameraControl._handlePointerUp(pEvent, _this.mInputState);
                //double click event
                if (_this.mDoubleClickCounter >= 2) {
                    _this.resetDoubleClickState();
                    _this.mTools._handlePointerDblClick(pEvent, _this.mInputState);
                    _this.mView.cameraControl._handlePointerDblClick(pEvent, _this.mInputState);
                }
                //deregister pointer
                _this.mInputState.deregisterPointer(pEvent.pointerId);
                pEvent.preventDefault();
            }
        };
        this.onPointerMove = function (pEvent) {
            if (_this.mInputState.updatePointer(pEvent)) {
                //reset double click state if pointer was moved
                var tPointer = _this.mInputState.pointers.get(pEvent.pointerId);
                if (tPointer != null &&
                    (Math.abs(tPointer.initialPosition.x - tPointer.position.x) > Viewer.POINTER_MOVE_TOLERANCE ||
                        Math.abs(tPointer.initialPosition.y - tPointer.position.y) > Viewer.POINTER_MOVE_TOLERANCE)) {
                    _this.resetDoubleClickState();
                }
            }
            _this.mTools._handlePointerMove(pEvent, _this.mInputState);
            _this.mView.cameraControl._handlePointerMove(pEvent, _this.mInputState);
            pEvent.preventDefault();
        };
        this.onPointerCancel = function (pEvent) {
            if (_this.mInputState.pointers.has(pEvent.pointerId)) {
                _this.mView.cameraControl._handlePointerCancel(pEvent, _this.mInputState);
                _this.mTools._handlePointerCancel(pEvent, _this.mInputState);
                _this.mInputState.deregisterPointer(pEvent.pointerId);
                _this.mCanvas.releasePointerCapture(pEvent.pointerId);
                _this.resetDoubleClickState();
            }
        };
        this.onKeyUp = function (pEvent) {
            _this.mTools._handleKeyUp(pEvent, _this.mInputState);
        };
        this.onMouseWheel = function (pEvent) {
            _this.mInputState._updatePointerPosition(pEvent);
            _this.mView.cameraControl._handleMouseWheel(pEvent, _this.mInputState);
            pEvent.preventDefault();
        };
        this.onContextMenu = function (pEvent) {
            //prevent browser context menu
            pEvent.preventDefault();
        };
        this.onScroll = function (pEvent) {
            _this.mClientOffset = null; //reset cached client offset
        };
        this.mApp = pApp;
        this.mContainer = pContainer;
        this.mRenderFrameRequested = true;
        this.mDisableRenderingDepth = 0;
        this.showPointerFeedback = true;
        this.onDemandRenderingEnabled = true;
        this.mInputState = new InputState();
        this.mDoubleClickCounter = 0;
        this.mDoubleClickTimer = null;
        this.mWidth = -1;
        this.mHeight = -1;
        this.mClientOffset = null;
        this.mResizeTimeOut = null;
        this.mNextMSAAStep = -1;
        this.mMSAAQuality = Viewer.MSAA_MAX_QUALITY;
        this.mMSAADelayFrame = 0;
        this.mSSAOQuality = "Medium";
        this.mFXAAEnabled = false;
        this.mScreenshotLock = new Lock();
        this.mPerformResizeAfterScreenshot = false;
        //container must be positioned relative
        this.mContainer.style.position = "relative";
        //create canvas
        this.mCanvas = document.createElement("canvas");
        this.mCanvas.style.width = this.getContainerWidth().toString() + "px";
        this.mCanvas.style.height = this.getContainerHeight().toString() + "px";
        this.mCanvas.style.display = "block";
        this.mCanvas.style.outline = "none";
        this.mCanvas.style.msUserSelect = "none";
        this.mCanvas.style.webkitUserSelect = "none";
        this.mCanvas.style.userSelect = "none";
        this.mCanvas.tabIndex = 0; //make canvas focusable
        this.mCanvas.setAttribute("touch-action", "none"); //required for jquery.PEP
        this.mContainer.appendChild(this.mCanvas);
    }
    Object.defineProperty(Viewer.prototype, "app", {
        get: function () {
            return (this.mApp);
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(Viewer.prototype, "container", {
        get: function () {
            return (this.mContainer);
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(Viewer.prototype, "canvas", {
        get: function () {
            return (this.mCanvas);
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(Viewer.prototype, "scene", {
        get: function () {
            return (this.mScene);
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(Viewer.prototype, "width", {
        get: function () {
            return (this.mWidth);
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(Viewer.prototype, "height", {
        get: function () {
            return (this.mHeight);
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(Viewer.prototype, "view", {
        get: function () {
            return (this.mView);
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(Viewer.prototype, "inputState", {
        get: function () {
            return (this.mInputState);
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(Viewer.prototype, "backgroundColor", {
        get: function () {
            return (this.mScene.clearColor);
        },
        set: function (pColor) {
            this.mScene.clearColor = pColor;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(Viewer.prototype, "renderingEnabled", {
        get: function () {
            return (this.mDisableRenderingDepth === 0);
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(Viewer.prototype, "antiAliasingQuality", {
        /**
         * defines the antialiasing quality, higher is better.
         * @valueRange [0, 32]
         * @defaultValue 32
         */
        get: function () {
            return (this.mMSAAQuality);
        },
        set: function (pQuality) {
            this.mMSAAQuality = clamp(pQuality, 0, Viewer.MSAA_MAX_QUALITY);
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(Viewer.prototype, "ssaoQuality", {
        /**
         * defines the SSAO quality or turns it off
         * @defaultValue "Medium"
         */
        get: function () {
            return (this.mSSAOQuality);
        },
        set: function (pValue) {
            if (this.mSSAOQuality === pValue)
                return;
            this.mSSAOQuality = pValue;
            if (this.mView != null) {
                this.mView._setSSAOQuality(this.mSSAOQuality);
            }
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(Viewer.prototype, "fxaaEnabled", {
        /**
         * Enables/disables FXAA. FXAA is used during user interaction to reduce aliasing.
         * Note: It is not recommended to use FXAA in combination with a transparent background because this leads to artifacts in some cases.
         * @defaultValue false
         */
        get: function () {
            return (this.mFXAAEnabled);
        },
        set: function (pValue) {
            if (this.mFXAAEnabled === pValue)
                return;
            this.mFXAAEnabled = pValue;
            if (this.mView != null) {
                this.mView._updateFXAA();
            }
        },
        enumerable: false,
        configurable: true
    });
    Viewer.prototype.initialize = function (pAutoResize) {
        if (this.mApp.model == null || this.mApp.tools == null)
            return (false);
        this.mApp.engine.canvasTabIndex = this.mCanvas.tabIndex;
        this.mScene = this.mApp.scene;
        this.mScene.clearColor = new Color4(1, 1, 1, 1);
        this.mTools = this.mApp.tools;
        this.mWidth = this.mCanvas.clientWidth;
        this.mHeight = this.mCanvas.clientHeight;
        this.mView = View._CreateView(this);
        this.mView.resize();
        this.mView._setSSAOQuality(this.mSSAOQuality);
        this.mScene.activeCamera = this.mView.camera;
        //register global resize event
        if (pAutoResize)
            window.addEventListener("resize", this.onResize);
        //register canvas events
        this.mCanvas.addEventListener("pointerdown", this.onPointerDown, false);
        this.mCanvas.addEventListener("pointerup", this.onPointerUp, false);
        this.mCanvas.addEventListener("pointermove", this.onPointerMove, false);
        //pointer cancel gets triggered only in very rare cases from IOS (e.g if the iPad is rotated while a pointer is down)
        this.mCanvas.addEventListener("pointercancel", this.onPointerCancel, false);
        this.mCanvas.addEventListener("keyup", this.onKeyUp, false);
        this.mCanvas.addEventListener("wheel", this.onMouseWheel, false);
        this.mCanvas.addEventListener("contextmenu", this.onContextMenu, false);
        //Workaround:
        //IOS doesn't trigger the up event on the canvas in some cases.
        //e.g. if two or more fingers are moved outside of the screen. (IOS bug?)
        //up and cancel are registered also on the window, to make sure that the pointer gets correctly deregistered
        window.addEventListener("pointerup", this.onPointerUp, false);
        window.addEventListener("pointercancel", this.onPointerCancel, false);
        //capture all scroll events to reset cached client offset
        window.addEventListener("scroll", this.onScroll, true);
        /**
         * BabylonJs calls focus() on the canvas without `preventScroll: true` when preventDefault is used.
         * This should not have side effects as preventDefault() is also called our event handlers of the Viewer.
         */
        this.mScene.preventDefaultOnPointerDown = false;
        this.mScene.preventDefaultOnPointerUp = false;
        return (true);
    };
    Viewer.prototype.dispose = function () {
        //not initialized?
        if (this.mScene == null)
            return;
        if (this.mView != null) {
            this.mView.dispose();
        }
        //remove events
        window.removeEventListener("resize", this.onResize);
        this.mCanvas.removeEventListener("pointerdown", this.onPointerDown, false);
        this.mCanvas.removeEventListener("pointerup", this.onPointerUp, false);
        this.mCanvas.removeEventListener("pointermove", this.onPointerMove, false);
        this.mCanvas.removeEventListener("pointercancel", this.onPointerCancel, false);
        this.mCanvas.removeEventListener("keyup", this.onKeyUp, false);
        this.mCanvas.removeEventListener("wheel", this.onMouseWheel, false);
        this.mCanvas.removeEventListener("contextmenu", this.onContextMenu, false);
        window.removeEventListener("pointerup", this.onPointerUp, false);
        window.removeEventListener("pointercancel", this.onPointerCancel, false);
        window.removeEventListener("scroll", this.onScroll, true);
    };
    /**
     * Resets the camera settings of the view.
     */
    Viewer.prototype.resetView = function () {
        this.mView.cameraControl.resetCamera(true, true);
    };
    /**
     * Resizes the gl context to fit the current container/canvas size.
     * @param pImmediateResize If true the resize will be performed immediately otherwise it
     * will be delayed in order to merge consecutive resize() calls. (default: false)
     */
    Viewer.prototype.resize = function (pImmediateResize) {
        var _this = this;
        if (pImmediateResize === void 0) { pImmediateResize = false; }
        if (pImmediateResize) {
            this.performResize();
        }
        else {
            if (this.mResizeTimeOut == null) {
                this.mResizeTimeOut = window.setTimeout(function () {
                    _this.performResize();
                }, 100);
            }
        }
    };
    /**
     * Has to be called if the viewer should render the scene. (e.g. after the camera has changed)
     */
    Viewer.prototype.requestRenderFrame = function () {
        this.mMSAADelayFrame = 0;
        this.mRenderFrameRequested = true;
    };
    /**
     * Forces immediate/synchronous rendering of the scene. This function should be used with care.
     * Under normal circumstances requestRenderFrame() should be used instead.
     */
    Viewer.prototype.forceRenderFrame = function () {
        this.mMSAADelayFrame = 0;
        this._renderFrame(true);
    };
    /**
     * Can be used to disable the rendering. Nested calls are supported.
     */
    Viewer.prototype.beginDisableRendering = function () {
        this.mDisableRenderingDepth++;
    };
    /**
     * Can be used to re-enable the rendering. Nested calls are supported.
     */
    Viewer.prototype.endDisableRendering = function () {
        if (this.mDisableRenderingDepth > 0)
            this.mDisableRenderingDepth--;
    };
    Viewer.prototype.clientToViewerCoords = function (pCoords) {
        if (pCoords == null)
            throw new Error(ERR_INVALID_ARGUMENT);
        var tCoords = Vector2.Zero();
        this.clientToViewerCoordsToRef(pCoords, tCoords);
        return (tCoords);
    };
    Viewer.prototype.clientToViewerCoordsToRef = function (pCoords, pTarget) {
        if (pCoords == null || pTarget == null)
            return;
        var tOffset = this.getClientOffset();
        pTarget.x = pCoords.x - tOffset.x;
        pTarget.y = pCoords.y - tOffset.y;
    };
    Viewer.prototype.viewerToClientCoords = function (pCoords) {
        if (pCoords == null)
            throw new Error(ERR_INVALID_ARGUMENT);
        var tCoords = Vector2.Zero();
        this.viewerToClientCoordsToRef(pCoords, tCoords);
        return (tCoords);
    };
    Viewer.prototype.viewerToClientCoordsToRef = function (pCoords, pTarget) {
        if (pCoords == null || pTarget == null)
            return;
        var tOffset = this.getClientOffset();
        pTarget.x = pCoords.x + tOffset.x;
        pTarget.y = pCoords.y + tOffset.y;
    };
    Viewer.prototype.createScreenshot = function (pOptions, pAsBlob) {
        return __awaiter(this, void 0, void 0, function () {
            var tLockContext, tImage;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (pOptions == null)
                            throw newError(ERR_INVALID_ARGUMENT);
                        return [4 /*yield*/, this.mScreenshotLock.acquireLock()];
                    case 1:
                        tLockContext = _a.sent();
                        _a.label = 2;
                    case 2:
                        _a.trys.push([2, , 4, 5]);
                        return [4 /*yield*/, this.internalCreateScreenshot(pOptions, pAsBlob)];
                    case 3:
                        tImage = _a.sent();
                        return [2 /*return*/, (tImage)];
                    case 4:
                        tLockContext.unlock();
                        if (this.mPerformResizeAfterScreenshot) {
                            this.performResize();
                        }
                        return [7 /*endfinally*/];
                    case 5: return [2 /*return*/];
                }
            });
        });
    };
    Viewer.prototype.serialize = function () {
        var tIOViewer = {};
        tIOViewer.views = new Array();
        var tIOView = this.mView.serialize();
        if (tIOView != null)
            tIOViewer.views.push(tIOView);
        return (tIOViewer);
    };
    Viewer.prototype.deserialize = function (pIOViewer) {
        if (pIOViewer != null && pIOViewer.views.length > 0) {
            this.mView.deserialize(pIOViewer.views[0]);
        }
        return (true);
    };
    /** @internal */
    Viewer.prototype._isScreenshotMesh = function (pMesh) {
        return (pMesh._wcf_core_isScreenshotMesh === true);
    };
    /** @internal */
    Viewer.prototype._renderFrame = function (pForce) {
        //disable rendering while creating screenshots
        if ((!this.renderingEnabled && !pForce) || this.mScreenshotLock.isLocked)
            return;
        //get camera changed flag
        var tCameraChanged = this.mView.cameraControl._beforeRender();
        var tNeedUpdateFrame = !this.onDemandRenderingEnabled || this.mRenderFrameRequested || tCameraChanged || pForce;
        if (tNeedUpdateFrame) {
            this.mNextMSAAStep = -1; //reset antialiasing
            this.eventBeforeRender.trigger();
        }
        this.mMSAADelayFrame++;
        var tNeedUpdateMSAA = this.mMSAADelayFrame > Viewer.MSAA_DELAY_FRAMES &&
            this.mNextMSAAStep <= this.mMSAAQuality && this.mMSAAQuality > 0;
        if (tNeedUpdateFrame || tNeedUpdateMSAA) {
            this.mView._prepareRenderFrame(this.mNextMSAAStep, this.mMSAAQuality);
            this.mScene.render();
            this.mView._finalizeRenderFrame(this.mNextMSAAStep, this.mMSAAQuality);
            this.mNextMSAAStep++;
            this.mRenderFrameRequested = false;
            if (tNeedUpdateFrame)
                this.eventAfterRender.trigger();
        }
    };
    /** @internal */
    Viewer.prototype._forwardEvent = function (pEvent, pOnlyUpdatePointerPosition) {
        if (pEvent == null)
            return;
        var tUpdatePointerPosition = false;
        switch (pEvent.type) {
            case "pointerdown":
                if (pOnlyUpdatePointerPosition)
                    tUpdatePointerPosition = true;
                else
                    this.onPointerDown(pEvent);
                break;
            case "wheel":
                if (pOnlyUpdatePointerPosition)
                    tUpdatePointerPosition = true;
                else
                    this.onMouseWheel(pEvent);
                break;
            case "click": //forwarded from button interactors
                tUpdatePointerPosition = true;
                break;
        }
        if (tUpdatePointerPosition) {
            this.mInputState._updatePointerPosition(pEvent);
        }
    };
    Viewer.prototype.getClientOffset = function () {
        if (this.mClientOffset == null) {
            var tRect = this.mCanvas.getBoundingClientRect();
            this.mClientOffset = new Vector2(tRect.left, tRect.top);
        }
        return (this.mClientOffset);
    };
    Viewer.prototype.getContainerWidth = function () {
        return (this.mContainer.clientWidth >= Viewer.MIN_CANVAS_SIZE ? this.mContainer.clientWidth : Viewer.MIN_CANVAS_SIZE);
    };
    Viewer.prototype.getContainerHeight = function () {
        return (this.mContainer.clientHeight >= Viewer.MIN_CANVAS_SIZE ? this.mContainer.clientHeight : Viewer.MIN_CANVAS_SIZE);
    };
    Viewer.prototype.performResize = function () {
        //do not resize while creating a screenshot
        if (this.mScreenshotLock.isLocked) {
            this.mPerformResizeAfterScreenshot = true;
            return;
        }
        else {
            this.mPerformResizeAfterScreenshot = false;
        }
        //Workaround:
        //If canvas deviceRatio is != 1 the canvas is rendered to big
        //after minimizing/maximzing the browser on IOS.
        //Using fixed pixel dimensions and delaying the resizing seems (see resize()) to prevent this issue.
        this.mResizeTimeOut = null;
        this.mClientOffset = null; //reset cached client offset
        var tNewWidth = this.getContainerWidth();
        var tNewHeight = this.getContainerHeight();
        this.mCanvas.style.width = tNewWidth.toString() + "px";
        this.mCanvas.style.height = tNewHeight.toString() + "px";
        if (this.mWidth != tNewWidth || this.mHeight != tNewHeight) {
            this.mWidth = tNewWidth;
            this.mHeight = tNewHeight;
            this.mApp.engine.resize();
            this.mView.resize();
            this.eventResize.trigger();
            this.requestRenderFrame();
        }
    };
    Viewer.prototype.createPointerFeedback = function (pEvent) {
        var _this = this;
        if (!this.showPointerFeedback)
            return;
        if (pEvent == null || PointerInfo.IsMousePointer(pEvent))
            return;
        var tPointer = this.mInputState.pointers.get(pEvent.pointerId);
        if (tPointer == null)
            return;
        var tViewerPos = this.clientToViewerCoords(tPointer.position);
        var tPointerFb = document.createElement("div");
        tPointerFb.className = "wcf-core pointer-feedback";
        tPointerFb.style.left = tViewerPos.x.toString() + "px";
        tPointerFb.style.top = tViewerPos.y.toString() + "px";
        this.mContainer.appendChild(tPointerFb);
        setTimeout(function () {
            _this.mContainer.removeChild(tPointerFb);
        }, 1000);
    };
    Viewer.prototype.internalCreateScreenshot = function (pOptions, pAsBlob) {
        return __awaiter(this, void 0, void 0, function () {
            var tOldActiveCamera, tOldLayerMask, tOldClearColor, tOldSizeOverride, tOldPixelScaleFactor, tOldCanvasWidth, tOldCanvasHeight, tOldSSAOQuality, tImageData, tOverlayCanvas, tWidth_1, tHeight_1, tNewWidth, tNewHeight, tCurrentMSAAStep_1, tRenderAsync_1, tMaxAASteps_1, tRenderPromise;
            var _this = this;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        tOldActiveCamera = this.mScene.activeCamera;
                        tOldLayerMask = this.mView.camera.layerMask;
                        tOldClearColor = this.mScene.clearColor;
                        tOldSizeOverride = this.mView._getSizeOverride();
                        tOldPixelScaleFactor = this.mView.pixelScaleFactor;
                        tOldCanvasWidth = this.mCanvas.width;
                        tOldCanvasHeight = this.mCanvas.height;
                        tOldSSAOQuality = this.mSSAOQuality;
                        tImageData = null;
                        tOverlayCanvas = this.createScreenshotOverlayCanvas();
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, , 3, 4]);
                        tWidth_1 = Math.round(pOptions.width);
                        tHeight_1 = Math.round(pOptions.height);
                        //adjust view size for correct rendering and projection calculations
                        this.mApp.engine.setSize(tWidth_1, tHeight_1);
                        this.mView._setSizeOverride(new Vector2(tWidth_1, tHeight_1), false);
                        //trigger before render
                        if (pOptions.onBeforeRender != null) {
                            pOptions.onBeforeRender();
                            tNewWidth = Math.round(pOptions.width);
                            tNewHeight = Math.round(pOptions.height);
                            if (tNewWidth != tWidth_1 || tNewHeight != tHeight_1) {
                                tWidth_1 = tNewWidth;
                                tHeight_1 = tNewHeight;
                                this.mApp.engine.setSize(tWidth_1, tHeight_1);
                                this.mView._setSizeOverride(new Vector2(tWidth_1, tHeight_1), false);
                            }
                        }
                        this.mScene.activeCamera = this.mView.camera;
                        this.mView.pixelScaleFactor = pOptions.pixelScaleFactor != null ? pOptions.pixelScaleFactor : 1.0;
                        this.mView.camera.layerMask = ViewLayerMaskFlags.Screenshot;
                        this.mView.camera.notifyProjectionChanged();
                        if (pOptions.backgroundColor != null)
                            this.mScene.clearColor = pOptions.backgroundColor;
                        if (pOptions.ssaoQuality != null) {
                            this.mView._setSSAOQuality(pOptions.ssaoQuality);
                        }
                        tCurrentMSAAStep_1 = 0;
                        tRenderAsync_1 = pOptions.renderAsync != null ? pOptions.renderAsync : false;
                        tMaxAASteps_1 = pOptions.antiAliasingQuality != null ? pOptions.antiAliasingQuality : Viewer.MSAA_MAX_QUALITY;
                        tMaxAASteps_1 = clamp(tMaxAASteps_1, 0, Viewer.MSAA_MAX_QUALITY);
                        //prepare meshes to render
                        this.markScreenshotMeshes(pOptions.meshesToRender, true);
                        this._renderOnlyScreenshotMeshes = pOptions.meshesToRender != null;
                        tRenderPromise = new Promise(function (resolve, reject) {
                            var renderFrame = function () {
                                _this.mView._prepareRenderFrame(tCurrentMSAAStep_1, tMaxAASteps_1);
                                _this.mScene.render();
                                _this.mView._finalizeRenderFrame(tCurrentMSAAStep_1, tMaxAASteps_1);
                                if (tCurrentMSAAStep_1 === tMaxAASteps_1) {
                                    _this.dumpFramebuffer(tWidth_1, tHeight_1, pOptions, pAsBlob)
                                        .then(function (pResult) { return resolve(pResult); })
                                        .catch(function (pError) { return reject(pError); });
                                }
                                tCurrentMSAAStep_1++;
                                if (tCurrentMSAAStep_1 <= tMaxAASteps_1) {
                                    //render next
                                    if (tRenderAsync_1)
                                        setTimeout(renderFrame);
                                    else
                                        renderFrame();
                                }
                            };
                            //render frames
                            renderFrame();
                        });
                        return [4 /*yield*/, tRenderPromise];
                    case 2:
                        tImageData = _a.sent();
                        return [2 /*return*/, (tImageData)];
                    case 3:
                        //clean up
                        this.mScene.clearColor = tOldClearColor;
                        this.mScene.activeCamera = tOldActiveCamera;
                        this.mView.pixelScaleFactor = tOldPixelScaleFactor;
                        this.mView._setSSAOQuality(tOldSSAOQuality);
                        this.mView.camera.layerMask = tOldLayerMask;
                        this.mView.camera.notifyProjectionChanged();
                        this.markScreenshotMeshes(pOptions.meshesToRender, false);
                        this._renderOnlyScreenshotMeshes = false;
                        if (pOptions.onAfterRender != null)
                            pOptions.onAfterRender();
                        this.mApp.engine.setSize(tOldCanvasWidth, tOldCanvasHeight);
                        this.mView._setSizeOverride(tOldSizeOverride, false);
                        //if viewer is currently rendering the MSAA frames we need to restart the MSAA process
                        if (this.mNextMSAAStep <= this.mMSAAQuality)
                            this.mNextMSAAStep = 0;
                        this.reomveScreenshotOverlayCanvas(tOverlayCanvas);
                        return [7 /*endfinally*/];
                    case 4: return [2 /*return*/];
                }
            });
        });
    };
    Viewer.prototype.markScreenshotMeshes = function (pMeshes, pValue) {
        if (pMeshes != null) {
            for (var _i = 0, pMeshes_1 = pMeshes; _i < pMeshes_1.length; _i++) {
                var tMesh = pMeshes_1[_i];
                tMesh._wcf_core_isScreenshotMesh = pValue;
            }
        }
    };
    Viewer.prototype.createScreenshotOverlayCanvas = function () {
        var tCanvas = document.createElement("canvas");
        tCanvas.width = this.mCanvas.width;
        tCanvas.height = this.mCanvas.height;
        tCanvas.style.position = "absolute";
        tCanvas.style.top = "0px";
        tCanvas.style.left = "0px";
        tCanvas.style.width = "100%";
        tCanvas.style.height = "100%";
        var tContext = tCanvas.getContext("2d");
        if (tContext == null)
            throw new Error("Failed to get canvas 2d context");
        tContext.drawImage(this.mCanvas, 0, 0);
        this.mView.overlayDiv.insertBefore(tCanvas, this.mView.overlayDiv.firstChild);
        this.mCanvas.style.visibility = "hidden";
        return (tCanvas);
    };
    Viewer.prototype.reomveScreenshotOverlayCanvas = function (pCanvas) {
        this.mCanvas.style.visibility = "visible";
        this.mView.overlayDiv.removeChild(pCanvas);
    };
    Viewer.prototype.dumpFramebuffer = function (pWidth, pHeight, pOptions, pAsBlob) {
        if (pAsBlob === void 0) { pAsBlob = false; }
        return __awaiter(this, void 0, void 0, function () {
            var tMimeType, tCanvas, tContext;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        tMimeType = pOptions.mimeType != null ? pOptions.mimeType : "image/png";
                        tCanvas = pOptions.targetCanvas;
                        if (tCanvas == null) {
                            if (this.mDumpFrameBufferCanvas == null)
                                this.mDumpFrameBufferCanvas = document.createElement('canvas');
                            tCanvas = this.mDumpFrameBufferCanvas;
                        }
                        tCanvas.width = pWidth;
                        tCanvas.height = pHeight;
                        tContext = tCanvas.getContext('2d');
                        if (tContext == null)
                            throw new Error("Failed to get canvas 2d context");
                        if (!(pOptions.onDrawBackground != null && this.mScene.clearColor.a < 0.99)) return [3 /*break*/, 2];
                        return [4 /*yield*/, pOptions.onDrawBackground(tContext)];
                    case 1:
                        _a.sent();
                        _a.label = 2;
                    case 2:
                        //draw the screenshot on the 2D canvas
                        tContext.drawImage(this.mCanvas, 0, 0);
                        if (!(pOptions.onDrawOverlay != null)) return [3 /*break*/, 4];
                        return [4 /*yield*/, pOptions.onDrawOverlay(tContext)];
                    case 3:
                        _a.sent();
                        _a.label = 4;
                    case 4:
                        //resolve with an empty string if a target canvas was defined
                        if (pOptions.targetCanvas != null) {
                            return [2 /*return*/, ("")];
                        }
                        if (pAsBlob) {
                            return [2 /*return*/, (canvasToBlob(tCanvas, tMimeType))];
                        }
                        else {
                            return [2 /*return*/, (tCanvas.toDataURL(tMimeType))];
                        }
                        return [2 /*return*/];
                }
            });
        });
    };
    Viewer.MAX_DOUBLE_CLICK_DELAY = 300;
    Viewer.POINTER_MOVE_TOLERANCE = (window.devicePixelRatio != null && window.devicePixelRatio > 1.0) ?
        window.devicePixelRatio * 3 : 3;
    //// private ///////////////////////////////////////////////////////////////////////////////
    Viewer.MSAA_DELAY_FRAMES = 20;
    Viewer.MSAA_MAX_QUALITY = 32;
    Viewer.MIN_CANVAS_SIZE = 4;
    return Viewer;
}());
export { Viewer };
